import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { eventosAPI, colmenasAPI, zonasAPI, exportAPI, fotosAPI, varroaAPI, avisosAPI } from '../api/client';
import { useTranslation } from 'react-i18next';
import VoiceInput from '../components/VoiceInput';

const TIPOS = [
  { value: 'inspeccion',    labelKey: 'bitacora.tipoInspeccion',   icon: '🔍', color: '#4A7C59' },
  { value: 'alimentacion',  labelKey: 'bitacora.tipoAlimentacion', icon: '🍯', color: '#E8922A' },
  { value: 'tratamiento',   labelKey: 'bitacora.tipoTratamiento',  icon: '💊', color: '#C0392B' },
  { value: 'cosecha',       labelKey: 'bitacora.tipoCosecha',      icon: '🫙', color: '#C97820' },
  { value: 'alza',          labelKey: 'bitacora.tipoAlza',         icon: '📦', color: '#6B4FA0' },
  { value: 'varroa',        labelKey: 'bitacora.tipoVarroa',       icon: '🔬', color: '#B7410E' },
  { value: 'nota',          labelKey: 'bitacora.tipoNota',         icon: '📝', color: '#5C3D2E' },
  { value: 'otro',          labelKey: 'bitacora.tipoOtro',         icon: '⚙️', color: '#8B6355' },
];

const tipoInfo = (tipo) => TIPOS.find(t => t.value === tipo) || TIPOS.find(t => t.value === 'nota');

// Catálogo de sustancias (códigos estables; el backend decide apto_bio).
const SUSTANCIAS = [
  { grupo: 'organicos', bio: true, items: ['ameisensaeure', 'oxalsaeure_traeufeln', 'oxalsaeure_sublimation', 'milchsaeure', 'thymol'], acido: ['ameisensaeure', 'oxalsaeure_traeufeln', 'oxalsaeure_sublimation', 'milchsaeure'] },
  { grupo: 'biotecnicos', bio: true, items: ['drohnenbrutentnahme', 'bannwaben', 'kunstschwarm'] },
  { grupo: 'sinteticos', bio: false, items: ['amitraz', 'flumethrin', 'coumaphos'] },
  { grupo: 'otroGrupo', bio: null, items: ['otro'] },
];
const bioDeSustancia = (s) => {
  for (const g of SUSTANCIAS) if (g.items.includes(s)) return g.bio;
  return null;
};

function getWeekRange(offset = 0) {
  const today = new Date();
  const dow = today.getDay();
  const diffToMon = dow === 0 ? -6 : 1 - dow;
  const start = new Date(today);
  start.setDate(today.getDate() + diffToMon + offset * 7);
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  return { start, end };
}

function descargar(blob, nombre) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = nombre; a.click();
  URL.revokeObjectURL(url);
}

export default function Bitacora() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.slice(0, 2) || 'es';

  // La ficha de colmena puede abrir la Bitácora ya filtrada (?colmena=ID) y,
  // opcionalmente, con el modal de registro abierto (&nuevo=1). Sin parámetros
  // el comportamiento es el de siempre.
  const [searchParams] = useSearchParams();
  const colmenaParam = searchParams.get('colmena') || '';
  const abrirNuevo = searchParams.get('nuevo') === '1';

  const [eventos, setEventos] = useState([]);
  const [eventosRecientes, setEventosRecientes] = useState([]);
  const [truncado, setTruncado] = useState(false);
  const [colmenas, setColmenas] = useState([]);
  const [zonas, setZonas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(abrirNuevo);
  const [editEvento, setEditEvento] = useState(null);
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroColmena, setFiltroColmena] = useState(colmenaParam);
  const [showExport, setShowExport] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [vistaPeriodo, setVistaPeriodo] = useState('mes');
  const [periodoAño, setPeriodoAño] = useState(new Date().getFullYear());
  const [periodoMes, setPeriodoMes] = useState(new Date().getMonth());
  const [periodoSemanaOffset, setPeriodoSemanaOffset] = useState(0);

  useEffect(() => {
    Promise.all([colmenasAPI.list(), zonasAPI.list()])
      .then(([c, z]) => { setColmenas(c.data); setZonas(z.data); });
  }, []);

  // Rango de fechas del periodo seleccionado: el filtrado por fecha lo hace el
  // servidor, así los años/meses antiguos llegan completos aunque el historial
  // total supere el tope de seguridad.
  const rangoPeriodo = () => {
    if (vistaPeriodo === 'año') {
      return {
        desde: new Date(periodoAño, 0, 1, 0, 0, 0, 0),
        hasta: new Date(periodoAño, 11, 31, 23, 59, 59, 999),
      };
    }
    if (vistaPeriodo === 'mes') {
      return {
        desde: new Date(periodoAño, periodoMes, 1, 0, 0, 0, 0),
        hasta: new Date(periodoAño, periodoMes + 1, 0, 23, 59, 59, 999),
      };
    }
    const { start, end } = getWeekRange(periodoSemanaOffset);
    return { desde: start, hasta: end };
  };

  const loadEventos = () => {
    const { desde, hasta } = rangoPeriodo();
    return eventosAPI.list({ fecha_desde: desde.toISOString(), fecha_hasta: hasta.toISOString() })
      .then(e => {
        setEventos(e.data);
        setTruncado(e.headers?.['x-eventos-truncados'] === '1');
      })
      .finally(() => setLoading(false));
  };

  // Últimos eventos sin filtrar por periodo: solo para saber cuándo fue el
  // último registro de cada colmena en la vista semanal.
  const loadRecientes = () =>
    eventosAPI.list({}).then(e => setEventosRecientes(e.data)).catch(() => {});

  useEffect(() => { loadEventos(); }, [vistaPeriodo, periodoAño, periodoMes, periodoSemanaOffset]); // eslint-disable-line
  useEffect(() => { loadRecientes(); }, []);

  const eliminar = async (id) => {
    if (!window.confirm(t('bitacora.deleteConfirm'))) return;
    await eventosAPI.delete(id);
    loadEventos(); loadRecientes();
  };

  const exportarCSV = async (tipo) => {
    setExportando(true);
    try {
      const r = await exportAPI.csv(tipo, lang);
      const suffix = tipo ? `_${tipo}` : '';
      descargar(r.data, `colmena_backup${suffix}_${new Date().toISOString().slice(0,10)}.csv`);
    } finally { setExportando(false); setShowExport(false); }
  };

  const exportarJSON = async (tipo) => {
    setExportando(true);
    try {
      const r = await exportAPI.json(tipo, lang);
      const suffix = tipo ? `_${tipo}` : '';
      descargar(r.data, `colmena_backup${suffix}_${new Date().toISOString().slice(0,10)}.json`);
    } finally { setExportando(false); setShowExport(false); }
  };

  const { start: wStart, end: wEnd } = getWeekRange(periodoSemanaOffset);

  // El periodo ya viene filtrado del servidor; aquí solo tipo y colmena.
  const eventosFiltrados = eventos.filter(e => {
    if (filtroTipo && e.tipo !== filtroTipo) return false;
    if (filtroColmena && e.colmena_id !== parseInt(filtroColmena)) return false;
    return true;
  });

  const lastEventPorColmena = {};
  eventosRecientes.forEach(e => {
    if (!e.colmena_id) return;
    const d = new Date(e.fecha || e.created_at);
    if (!lastEventPorColmena[e.colmena_id] || d > lastEventPorColmena[e.colmena_id]) {
      lastEventPorColmena[e.colmena_id] = d;
    }
  });

  const grupos = eventosFiltrados.reduce((acc, e) => {
    const displayDate = e.fecha || e.created_at;
    const fecha = new Date(displayDate).toLocaleDateString(lang, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    if (!acc[fecha]) acc[fecha] = [];
    acc[fecha].push(e);
    return acc;
  }, {});

  return (
    <div>
      <div style={S.header}>
        <div>
          <h1 style={S.title}>{t('bitacora.title')}</h1>
          <p style={S.sub}>{t('bitacora.recordCount', { count: eventosFiltrados.length, s: eventosFiltrados.length !== 1 ? 's' : '' })}</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ position: 'relative' }}>
            <button className="btn btn-ghost" onClick={() => setShowExport(!showExport)}
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              ↓ {t('bitacora.exportBtn')}
            </button>
            {showExport && (
              <div style={S.exportMenu}>
                <button onClick={() => exportarCSV()} disabled={exportando} style={S.exportItem}>📊 {t('bitacora.exportAllCSV')}</button>
                <button onClick={() => exportarJSON()} disabled={exportando} style={S.exportItem}>📦 {t('bitacora.exportAllJSON')}</button>
                <div style={{ borderTop: '1px solid #FAE0B0', margin: '4px 0' }} />
                <button onClick={() => exportarCSV('cosecha')} disabled={exportando} style={S.exportItem}>{t('bitacora.exportHarvest')}</button>
                <button onClick={() => exportarCSV('tratamiento')} disabled={exportando} style={S.exportItem}>{t('bitacora.exportTreatment')}</button>
                <button onClick={() => exportarCSV('inspeccion')} disabled={exportando} style={S.exportItem}>{t('bitacora.exportInspection')}</button>
                <button onClick={() => exportarCSV('alimentacion')} disabled={exportando} style={S.exportItem}>{t('bitacora.exportFeeding')}</button>
              </div>
            )}
          </div>
          <button className="btn btn-primary" onClick={() => setModal(true)}>{t('bitacora.newBtn')}</button>
        </div>
      </div>

      <div style={{ marginBottom: 16, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={S.periodoTabs}>
          {[['mes', t('bitacora.vistaMes')], ['año', t('bitacora.vistaAño')], ['semana', t('bitacora.vistaSemana')]].map(([v, label]) => (
            <button key={v} onClick={() => setVistaPeriodo(v)}
              style={{ ...S.periodoTab, ...(vistaPeriodo === v ? S.periodoTabActive : {}) }}>
              {label}
            </button>
          ))}
        </div>
        {vistaPeriodo === 'año' && (
          <select value={periodoAño} onChange={e => setPeriodoAño(Number(e.target.value))} className="form-input" style={{ width: 'auto', minWidth: 90 }}>
            {[...Array(6)].map((_, i) => { const y = new Date().getFullYear() - i; return <option key={y} value={y}>{y}</option>; })}
          </select>
        )}
        {vistaPeriodo === 'mes' && (
          <>
            <select value={periodoMes} onChange={e => setPeriodoMes(Number(e.target.value))} className="form-input" style={{ width: 'auto', minWidth: 110 }}>
              {[...Array(12)].map((_, i) => (
                <option key={i} value={i}>{new Date(2000, i, 1).toLocaleDateString(lang, { month: 'long' })}</option>
              ))}
            </select>
            <select value={periodoAño} onChange={e => setPeriodoAño(Number(e.target.value))} className="form-input" style={{ width: 'auto', minWidth: 90 }}>
              {[...Array(6)].map((_, i) => { const y = new Date().getFullYear() - i; return <option key={y} value={y}>{y}</option>; })}
            </select>
          </>
        )}
        {vistaPeriodo === 'semana' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button onClick={() => setPeriodoSemanaOffset(v => v - 1)} style={S.navBtn}>←</button>
            <span style={{ fontSize: 13, color: '#5C3D2E', fontWeight: 500, minWidth: 180, textAlign: 'center' }}>
              {wStart.toLocaleDateString(lang, { day: '2-digit', month: 'short' })} – {wEnd.toLocaleDateString(lang, { day: '2-digit', month: 'short', year: 'numeric' })}
            </span>
            <button onClick={() => setPeriodoSemanaOffset(v => v + 1)} disabled={periodoSemanaOffset >= 0}
              style={{ ...S.navBtn, opacity: periodoSemanaOffset >= 0 ? 0.4 : 1, cursor: periodoSemanaOffset >= 0 ? 'default' : 'pointer' }}>→</button>
          </div>
        )}
      </div>

      <div style={S.filtros}>
        <div style={S.tipoFiltros}>
          <button onClick={() => setFiltroTipo('')}
            style={{ ...S.tipoPill, ...(filtroTipo === '' ? S.tipoPillActive : {}) }}>
            {t('bitacora.allTypes')}
          </button>
          {TIPOS.map(tp => (
            <button key={tp.value} onClick={() => setFiltroTipo(tp.value)}
              style={{ ...S.tipoPill, ...(filtroTipo === tp.value ? { ...S.tipoPillActive, background: tp.color + '22', color: tp.color, borderColor: tp.color } : {}) }}>
              {tp.icon} {t(tp.labelKey)}
            </button>
          ))}
        </div>
        <select className="form-input" style={{ width: 'auto', minWidth: 160 }}
          value={filtroColmena} onChange={e => setFiltroColmena(e.target.value)}>
          <option value="">{t('bitacora.allColmenas')}</option>
          {colmenas.filter(c => c.activa).map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
        </select>
      </div>

      {truncado && (
        <div style={S.truncadoAviso} role="alert">
          ⚠️ {t('bitacora.truncadoAviso')}
        </div>
      )}

      {loading ? <div style={{ textAlign: 'center', padding: 60, color: '#8B6355' }}>{t('common.loading')}</div>
        : vistaPeriodo === 'semana' ? (
          <div>
            {colmenas.filter(c => c.activa).length === 0 ? (
              <div className="empty-state">
                <div className="icon">📓</div>
                <h3>{t('bitacora.emptyTitle')}</h3>
              </div>
            ) : colmenas.filter(c => c.activa).map(c => {
              const cEvents = eventosFiltrados.filter(e => e.colmena_id === c.id);
              const lastDate = lastEventPorColmena[c.id];
              const daysSince = lastDate ? Math.floor((new Date() - lastDate) / 86400000) : null;
              return (
                <div key={c.id} style={{ marginBottom: 20 }}>
                  <div style={S.fechaLabel}>
                    🐝 {c.nombre}{c.zona_nombre ? <span style={{ fontWeight: 400, opacity: 0.7 }}> · {c.zona_nombre}</span> : null}
                  </div>
                  {cEvents.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {cEvents.map(e => <EventoCard key={e.id} evento={e} onEliminar={() => eliminar(e.id)} onEditar={() => { setEditEvento(e); setModal(true); }} lang={lang} />)}
                    </div>
                  ) : (
                    <div style={S.sinActividad}>
                      <span style={{ fontSize: 18 }}>🕐</span>
                      <div>
                        <div style={{ fontWeight: 500, fontSize: 13, color: '#8B6355' }}>{t('bitacora.sinActividad')}</div>
                        <div style={{ fontSize: 12, color: '#C0A090', marginTop: 2 }}>
                          {daysSince === null
                            ? t('bitacora.sinRegistros')
                            : t('bitacora.ultimoRegistro', { dias: daysSince, s: daysSince !== 1 ? 's' : '' })}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : eventosFiltrados.length === 0 ? (
          <div className="empty-state">
            <div className="icon">📓</div>
            <h3>{t('bitacora.emptyTitle')}</h3>
            <p>{t('bitacora.emptyMsg')}</p>
            <button className="btn btn-primary" onClick={() => setModal(true)}>{t('bitacora.newBtn')}</button>
          </div>
        ) : (
          <div>
            {Object.entries(grupos).map(([fecha, evs]) => (
              <div key={fecha} style={{ marginBottom: 24 }}>
                <div style={S.fechaLabel}>{fecha}</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {evs.map(e => <EventoCard key={e.id} evento={e} onEliminar={() => eliminar(e.id)} onEditar={() => { setEditEvento(e); setModal(true); }} lang={lang} />)}
                </div>
              </div>
            ))}
          </div>
        )}

      {modal && (
        <EventoModal colmenas={colmenas} zonas={zonas} evento={editEvento}
          colmenaInicial={!editEvento && colmenaParam ? parseInt(colmenaParam) : null}
          onSave={async (data, skipClose = false) => {
            if (editEvento) {
              await eventosAPI.update(editEvento.id, data);
            } else {
              await eventosAPI.create(data);
            }
            if (!skipClose) {
              setModal(false);
              setEditEvento(null);
              loadEventos(); loadRecientes();
            }
          }}
          onClose={() => { setModal(false); setEditEvento(null); }} />
      )}
    </div>
  );
}

function EventoCard({ evento: e, onEliminar, onEditar, lang }) {
  const { t } = useTranslation();
  const tp = tipoInfo(e.tipo);
  const displayDate = e.fecha || e.created_at;
  const hora = new Date(displayDate).toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit' });
  return (
    <div style={S.eventoCard}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <div style={{ ...S.tipoIcon, background: tp.color + '18', color: tp.color }}>{tp.icon}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <span style={{ fontWeight: 500, fontSize: 15 }}>{e.titulo}</span>
              <span style={{ ...S.tipoBadge, background: tp.color + '15', color: tp.color }}>{t(tp.labelKey)}</span>
            </div>
            <span style={{ fontSize: 12, color: '#C0A090', flexShrink: 0, marginLeft: 12 }}>{hora}</span>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
            {e.colmena_nombre && <span className="badge badge-amber">🐝 {e.colmena_nombre}</span>}
            {e.zona_nombre && !e.colmena_nombre && <span className="badge" style={{ background: '#F0EDE9', color: '#5C3D2E' }}>📍 {e.zona_nombre}</span>}
          </div>
          {e.descripcion && <p style={{ fontSize: 13, color: '#5C3D2E', marginTop: 6, lineHeight: 1.5 }}>{e.descripcion}</p>}
          {e.tipo === 'inspeccion' && (e.comportamiento != null || e.alimento_cantidad != null) && (
            <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
              {e.comportamiento && (
                <span style={S.infoChip}>
                  {[null, t('bitacora.behaviorAggressive'), t('bitacora.behaviorNervous'), t('bitacora.behaviorCalm'), t('bitacora.behaviorDocile')][e.comportamiento]}
                </span>
              )}
              {e.alimento_cantidad != null && (
                <span style={S.infoChip}>
                  🍯 {e.alimento_cantidad} {e.alimento_unidad}
                </span>
              )}
            </div>
          )}
          {e.tipo === 'cosecha' && e.cosecha_kg != null && (
            <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
              <span style={{ ...S.infoChip, background: '#FFF4E0', color: '#C97820', borderColor: '#F5D9A8' }}>
                🍯 {e.cosecha_kg} kg
              </span>
              {e.cosecha_kg === 0 && e.cosecha_motivo && (
                <span style={S.infoChip}>
                  {e.cosecha_motivo === 'otro'
                    ? (e.cosecha_motivo_otro || t('cosecha.motivo_otro'))
                    : t(`cosecha.motivo_${e.cosecha_motivo}`)}
                </span>
              )}
            </div>
          )}
          {e.tipo === 'tratamiento' && e.tratamiento_detalle && (
            <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
              <span style={{ ...S.infoChip, background: '#FDECEA', color: '#C0392B', borderColor: '#F5C6C0' }}>
                💊 {e.tratamiento_detalle.sustancia === 'otro'
                  ? (e.tratamiento_detalle.sustancia_otro || t('tratamiento.sust_otro'))
                  : t(`tratamiento.sust_${e.tratamiento_detalle.sustancia}`)}
              </span>
              {e.tratamiento_detalle.apto_bio === true && (
                <span style={{ ...S.infoChip, background: '#EAF7EE', color: '#1E8449', borderColor: '#A9DFBF' }}>🌿 {t('tratamiento.badgeBio')}</span>
              )}
              {e.tratamiento_detalle.apto_bio === false && (
                <span style={{ ...S.infoChip, background: '#FDECEA', color: '#C0392B', borderColor: '#F5B7B1' }}>⚠️ {t('tratamiento.badgeNoBio')}</span>
              )}
              {e.tratamiento_detalle.apto_bio == null && (
                <span style={{ ...S.infoChip, background: '#FDF6E3', color: '#7D6608', borderColor: '#FAE5A0' }}>❔ {t('tratamiento.badgeVerificar')}</span>
              )}
              {e.tratamiento_detalle.dosis && <span style={S.infoChip}>{e.tratamiento_detalle.dosis}</span>}
            </div>
          )}
          {e.tipo === 'varroa' && e.varroa_detalle && (
            <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
              <span style={{ ...S.infoChip, background: '#FBEAE2', color: '#B7410E', borderColor: '#F0C4AE' }}>
                🔬 {t(`varroa.metodoCorto_${e.varroa_detalle.metodo}`)}
              </span>
              {e.varroa_detalle.valor_calculado != null && (
                <span style={S.infoChip}>
                  {e.varroa_detalle.valor_calculado} {e.varroa_detalle.unidad === 'pct' ? '%' : t('varroa.unidadAcarosDia')}
                </span>
              )}
            </div>
          )}
          {e.tipo === 'alza' && e.alza_detalle && (
            <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
              <span style={{
                ...S.infoChip,
                background: e.alza_detalle.accion === 'colocada' ? '#EDE8F8' : '#F0EDE9',
                color: e.alza_detalle.accion === 'colocada' ? '#6B4FA0' : '#5C3D2E',
                borderColor: e.alza_detalle.accion === 'colocada' ? '#C8B8E8' : '#EAE0D0',
              }}>
                {e.alza_detalle.accion === 'colocada' ? t('bitacora.alzaColocadaTag') : t('bitacora.alzaRetiradaTag')}
              </span>
              <span style={S.infoChip}>
                📦 {e.alza_detalle.cantidad_alzas} {t('bitacora.tipoAlza')}
              </span>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flexShrink: 0 }}>
          <button onClick={onEditar} style={S.editBtn} title={t('bitacora.editBtn')}>✏️</button>
          <button onClick={onEliminar} style={S.deleteBtn} title={t('bitacora.deleteBtn')}>✕</button>
        </div>
      </div>
      <FotosSection eventoId={e.id} />
    </div>
  );
}

function FotosSection({ eventoId }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [fotos, setFotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [visor, setVisor] = useState(null);
  const camRef = useRef(null);
  const galRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fotosAPI.list(eventoId);
      setFotos(r.data);
    } catch (err) {
      console.error('Error cargando fotos', err);
    } finally {
      setLoading(false);
    }
  };

  const toggle = () => {
    if (!open) load();
    setOpen(o => !o);
  };

  const handleFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      await fotosAPI.upload(eventoId, files);
      await load();
    } catch (err) {
      console.error('Error subiendo foto', err);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const eliminar = async (fotoId) => {
    try {
      await fotosAPI.delete(fotoId);
      setFotos(f => f.filter(x => x.id !== fotoId));
      if (visor?.id === fotoId) setVisor(null);
    } catch (err) {
      console.error('Error eliminando foto', err);
    }
  };

  return (
    <div style={{ borderTop: '1px solid #F0EDE9', marginTop: 10, paddingTop: 8 }}>
      <button onClick={toggle} style={S.fotosToggle}>
        <span>📷 {t('bitacora.photos')}{fotos.length > 0 ? ` (${fotos.length})` : ''}</span>
        <span style={{ fontSize: 10, marginLeft: 4, opacity: 0.7 }}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div style={{ marginTop: 10 }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
            <input ref={camRef} type="file" accept="image/*" capture="environment" multiple
              style={{ display: 'none' }} onChange={handleFile} />
            <input ref={galRef} type="file" accept="image/*" multiple
              style={{ display: 'none' }} onChange={handleFile} />
            <button onClick={() => camRef.current?.click()} disabled={uploading} style={S.addFotoBtn}>
              📷 {t('bitacora.photoCamera')}
            </button>
            <button onClick={() => galRef.current?.click()} disabled={uploading} style={{ ...S.addFotoBtn, borderColor: '#FAE0B0', color: '#8B6355' }}>
              🖼️ {t('bitacora.photoGallery')}
            </button>
            {uploading && <span style={{ fontSize: 12, color: '#C97820', alignSelf: 'center' }}>{t('bitacora.photoUploading')}</span>}
          </div>

          {loading ? (
            <div style={{ fontSize: 12, color: '#8B6355', padding: '8px 0' }}>{t('bitacora.photoLoading')}</div>
          ) : fotos.length === 0 ? (
            <div style={{ fontSize: 12, color: '#C0A090', fontStyle: 'italic', padding: '4px 0' }}>{t('bitacora.photoNone')}</div>
          ) : (
            <div style={S.fotosGrid}>
              {fotos.map(f => (
                <div key={f.id} onClick={() => setVisor(f)} style={S.fotoThumb}>
                  <img
                    src={fotosAPI.imagenUrl(f.id)}
                    alt={f.filename}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {visor && (
        <div style={S.visorOverlay} onClick={() => setVisor(null)}>
          <div style={S.visorContent} onClick={ev => ev.stopPropagation()}>
            <img
              src={fotosAPI.imagenUrl(visor.id)}
              alt={visor.filename}
              style={{ maxWidth: '90vw', maxHeight: '75vh', objectFit: 'contain', borderRadius: 8, display: 'block' }}
            />
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 8, textAlign: 'center' }}>{visor.filename}</div>
            <div style={{ display: 'flex', gap: 10, marginTop: 14, justifyContent: 'center' }}>
              <button
                onClick={() => eliminar(visor.id)}
                style={{ background: '#FDECEA', color: '#C0392B', border: 'none', borderRadius: 8, padding: '8px 20px', cursor: 'pointer', fontSize: 14, fontFamily: "'DM Sans', sans-serif" }}
              >
                {t('bitacora.photoDelete')}
              </button>
              <button
                onClick={() => setVisor(null)}
                style={{ background: 'rgba(255,255,255,0.15)', color: 'white', border: 'none', borderRadius: 8, padding: '8px 20px', cursor: 'pointer', fontSize: 14, fontFamily: "'DM Sans', sans-serif" }}
              >
                {t('bitacora.photoClose')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function EventoModal({ colmenas, zonas, onSave, onClose, evento, colmenaInicial }) {
  const { t } = useTranslation();
  const isEdit = !!evento;
  const initAlcance = evento ? (evento.colmena_id ? 'colmena' : evento.zona_id ? 'zona' : 'general') : 'colmena';
  const initFecha = evento && evento.fecha ? new Date(evento.fecha).toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    tipo: evento?.tipo || 'inspeccion',
    titulo: evento?.titulo || '',
    descripcion: evento?.descripcion || '',
    zona_id: evento?.zona_id ? String(evento.zona_id) : '',
    alcance: initAlcance,
    fecha: initFecha,
    comportamiento: evento?.comportamiento || null,
    alimento_cantidad: evento?.alimento_cantidad != null ? String(evento.alimento_cantidad) : '',
    alimento_unidad: evento?.alimento_unidad || 'kg',
    alza_accion: evento?.alza_detalle?.accion || 'colocada',
    alza_cantidad: evento?.alza_detalle?.cantidad_alzas ? String(evento.alza_detalle.cantidad_alzas) : '1',
    alza_notas: evento?.alza_detalle?.notas || '',
    varroa_metodo: evento?.varroa_detalle?.metodo || 'gemuell',
    varroa_acaros: evento?.varroa_detalle?.acaros != null ? String(evento.varroa_detalle.acaros) : '',
    varroa_muestra: evento?.varroa_detalle?.muestra_abejas != null ? String(evento.varroa_detalle.muestra_abejas) : '100',
    varroa_fecha_colocacion: evento?.varroa_detalle?.fecha_colocacion ? new Date(evento.varroa_detalle.fecha_colocacion).toISOString().slice(0, 10) : '',
    varroa_fecha_lectura: evento?.varroa_detalle?.fecha_lectura ? new Date(evento.varroa_detalle.fecha_lectura).toISOString().slice(0, 10) : initFecha,
    trat_sustancia: evento?.tratamiento_detalle?.sustancia || '',
    trat_sustancia_otro: evento?.tratamiento_detalle?.sustancia_otro || '',
    trat_dosis: evento?.tratamiento_detalle?.dosis || '',
    trat_fecha_fin: evento?.tratamiento_detalle?.fecha_fin ? new Date(evento.tratamiento_detalle.fecha_fin).toISOString().slice(0, 10) : '',
    cosecha_kg: evento?.cosecha_kg != null ? String(evento.cosecha_kg) : '',
    cosecha_motivo: evento?.cosecha_motivo || '',
    cosecha_motivo_otro: evento?.cosecha_motivo_otro || '',
  });
  const [varroaEval, setVarroaEval] = useState(null);
  const [avisosCarencia, setAvisosCarencia] = useState([]);
  const [colmenasSelec, setColmenasSelec] = useState(() => {
    if (evento?.colmena_id) return new Set([evento.colmena_id]);
    if (colmenaInicial) return new Set([colmenaInicial]);
    return new Set();
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Evaluación orientativa en vivo del conteo de varroa (nunca un veredicto).
  const primeraColmena = colmenasSelec.size > 0 ? Array.from(colmenasSelec)[0] : null;
  useEffect(() => {
    if (form.tipo !== 'varroa') { setVarroaEval(null); return; }
    const metodo = form.varroa_metodo;
    const acaros = form.varroa_acaros !== '' ? parseInt(form.varroa_acaros) : null;
    const listo = metodo === 'drohnenbrut'
      ? true
      : metodo === 'gemuell'
        ? (acaros != null && form.varroa_fecha_colocacion && form.varroa_fecha_lectura)
        : (acaros != null && form.varroa_muestra !== '');
    if (!listo) { setVarroaEval(null); return; }
    const timer = setTimeout(() => {
      varroaAPI.evaluar({
        metodo,
        colmena_id: primeraColmena,
        acaros,
        muestra_abejas: metodo === 'puderzucker' || metodo === 'alcohol' ? parseInt(form.varroa_muestra) || null : null,
        fecha_colocacion: metodo === 'gemuell' && form.varroa_fecha_colocacion ? new Date(form.varroa_fecha_colocacion + 'T12:00:00').toISOString() : null,
        fecha_lectura: metodo === 'gemuell' && form.varroa_fecha_lectura ? new Date(form.varroa_fecha_lectura + 'T12:00:00').toISOString() : null,
        fecha: form.fecha ? new Date(form.fecha + 'T12:00:00').toISOString() : null,
      }).then(r => setVarroaEval(r.data)).catch(() => setVarroaEval(null));
    }, 400);
    return () => clearTimeout(timer);
  }, [form.tipo, form.varroa_metodo, form.varroa_acaros, form.varroa_muestra,
      form.varroa_fecha_colocacion, form.varroa_fecha_lectura, form.fecha, primeraColmena]);

  // Avisos de carencia (no bloqueantes): protegen la miel Bio.
  // Aviso 1: tratamiento con ácido y alza colocada. Aviso 2: cosecha tras tratamiento reciente.
  // Los ácidos se derivan del catálogo compartido (todas las variantes: fórmico, oxálico, láctico).
  const ACIDOS = SUSTANCIAS.flatMap(g => g.acido || []);
  const colmenasKey = Array.from(colmenasSelec).sort().join(',');
  useEffect(() => {
    const ids = colmenasKey ? colmenasKey.split(',').map(Number) : [];
    const necesitaAviso =
      (form.tipo === 'tratamiento' && ACIDOS.includes(form.trat_sustancia)) ||
      form.tipo === 'cosecha';
    if (!necesitaAviso || ids.length === 0) { setAvisosCarencia([]); return; }
    const contexto = form.tipo === 'tratamiento' ? 'tratamiento' : 'cosecha';
    const fecha = form.fecha ? new Date(form.fecha + 'T12:00:00').toISOString() : null;
    let cancelado = false;
    const timer = setTimeout(() => {
      Promise.all(ids.map(id =>
        avisosAPI.carencia(id, { contexto, ...(fecha ? { fecha } : {}) })
          .then(r => ({ colmena_id: id, ...r.data }))
          .catch(() => null)
      )).then(res => {
        if (cancelado) return;
        setAvisosCarencia(res.filter(a => a && (a.alza_activa || a.tratamiento_reciente)));
      });
    }, 300);
    return () => { cancelado = true; clearTimeout(timer); };
  }, [form.tipo, form.trat_sustancia, form.fecha, colmenasKey]); // eslint-disable-line

  const nombreColmena = (id) => colmenas.find(c => c.id === id)?.nombre || `#${id}`;
  const fmtFecha = (iso) => new Date(iso).toLocaleDateString();

  const toggleColmena = (id) => {
    setColmenasSelec(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleTodas = () => {
    const activas = colmenas.filter(c => c.activa).map(c => c.id);
    setColmenasSelec(prev => prev.size === activas.length ? new Set() : new Set(activas));
  };

  const submit = async e => {
    e.preventDefault();
    if (form.alcance === 'colmena' && colmenasSelec.size === 0) return;
    setSaving(true);
    try {
      const base = {
        tipo: form.tipo,
        titulo: form.titulo,
        descripcion: form.descripcion || null,
        zona_id: form.alcance === 'zona' && form.zona_id ? parseInt(form.zona_id) : null,
        fecha: form.fecha ? new Date(form.fecha + 'T12:00:00').toISOString() : null,
        comportamiento: form.tipo === 'inspeccion' && form.comportamiento ? form.comportamiento : null,
        alimento_cantidad: form.tipo === 'inspeccion' && form.alimento_cantidad !== '' ? parseFloat(form.alimento_cantidad) : null,
        alimento_unidad: form.tipo === 'inspeccion' && form.alimento_cantidad !== '' ? form.alimento_unidad : null,
        alza_accion: form.tipo === 'alza' ? form.alza_accion : null,
        alza_cantidad: form.tipo === 'alza' ? parseInt(form.alza_cantidad) : null,
        alza_notas: form.tipo === 'alza' && form.alza_notas ? form.alza_notas : null,
        varroa_metodo: form.tipo === 'varroa' ? form.varroa_metodo : null,
        varroa_acaros: form.tipo === 'varroa' && form.varroa_metodo !== 'drohnenbrut' && form.varroa_acaros !== '' ? parseInt(form.varroa_acaros) : null,
        varroa_muestra_abejas: form.tipo === 'varroa' && (form.varroa_metodo === 'puderzucker' || form.varroa_metodo === 'alcohol') && form.varroa_muestra !== '' ? parseInt(form.varroa_muestra) : null,
        varroa_fecha_colocacion: form.tipo === 'varroa' && form.varroa_metodo === 'gemuell' && form.varroa_fecha_colocacion ? new Date(form.varroa_fecha_colocacion + 'T12:00:00').toISOString() : null,
        varroa_fecha_lectura: form.tipo === 'varroa' && form.varroa_metodo === 'gemuell' && form.varroa_fecha_lectura ? new Date(form.varroa_fecha_lectura + 'T12:00:00').toISOString() : null,
        tratamiento_sustancia: form.tipo === 'tratamiento' && form.trat_sustancia ? form.trat_sustancia : null,
        tratamiento_sustancia_otro: form.tipo === 'tratamiento' && form.trat_sustancia === 'otro' && form.trat_sustancia_otro ? form.trat_sustancia_otro : null,
        tratamiento_dosis: form.tipo === 'tratamiento' && form.trat_dosis ? form.trat_dosis : null,
        tratamiento_fecha_fin: form.tipo === 'tratamiento' && form.trat_fecha_fin ? new Date(form.trat_fecha_fin + 'T12:00:00').toISOString() : null,
        // 0 kg es válido y distinto de "no registrado" (vacío ⇒ null).
        cosecha_kg: form.tipo === 'cosecha' && form.cosecha_kg !== '' ? parseFloat(form.cosecha_kg) : null,
        cosecha_motivo: form.tipo === 'cosecha' && form.cosecha_kg !== '' && parseFloat(form.cosecha_kg) === 0 && form.cosecha_motivo ? form.cosecha_motivo : null,
        cosecha_motivo_otro: form.tipo === 'cosecha' && form.cosecha_kg !== '' && parseFloat(form.cosecha_kg) === 0 && form.cosecha_motivo === 'otro' && form.cosecha_motivo_otro ? form.cosecha_motivo_otro : null,
      };
      if (form.alcance === 'colmena') {
        const ids = Array.from(colmenasSelec);
        for (const cid of ids) {
          await onSave({ ...base, colmena_id: cid }, ids.indexOf(cid) < ids.length - 1);
        }
      } else {
        await onSave({ ...base, colmena_id: null });
      }
    } finally { setSaving(false); }
  };

  const activasCount = colmenas.filter(c => c.activa).length;
  const todasSelec = colmenasSelec.size === activasCount && activasCount > 0;

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <h2>{isEdit ? t('bitacora.modalEdit') : t('bitacora.modalCreate')}</h2>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={submit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">{t('bitacora.tipoLabel')}</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8 }}>
                {TIPOS.map(tp => (
                  <button key={tp.value} type="button" onClick={() => set('tipo', tp.value)}
                    style={{ ...S.tipoBtn, ...(form.tipo === tp.value ? { background: tp.color, color: 'white', borderColor: tp.color } : {}) }}>
                    {tp.icon} {t(tp.labelKey)}
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">{t('bitacora.titleLabel')} *</label>
              <input value={form.titulo} onChange={e => set('titulo', e.target.value)}
                className="form-input" placeholder={t('bitacora.titlePlaceholder')} required />
            </div>

            <div className="form-group">
              <label className="form-label">{t('bitacora.dateLabel')}</label>
              <input type="date" value={form.fecha} onChange={e => set('fecha', e.target.value)}
                className="form-input" />
            </div>

            <div className="form-group">
              <label className="form-label">{t('bitacora.assignTo')}</label>
              <select value={form.alcance} onChange={e => set('alcance', e.target.value)} className="form-input">
                <option value="colmena">{t('bitacora.scopeHives')}</option>
                <option value="zona">{t('bitacora.scopeZone')}</option>
                <option value="general">{t('bitacora.scopeGeneral')}</option>
              </select>
            </div>

            {form.alcance === 'colmena' && (
              <div className="form-group">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 6 }}>
                  <label className="form-label" style={{ marginBottom: 0 }}>
                    {t('bitacora.colmenaLabel')}
                    {colmenasSelec.size > 0 && (
                      <span style={S.selecBadge}>{t('bitacora.selectedCount', { count: colmenasSelec.size, s: colmenasSelec.size > 1 ? 's' : '' })}</span>
                    )}
                  </label>
                  {activasCount > 1 && (
                    <button type="button" onClick={toggleTodas} style={S.toggleTodasBtn}>
                      {todasSelec ? t('bitacora.deselectAllHives') : t('bitacora.selectAllHives')}
                    </button>
                  )}
                </div>
                <div style={S.checkList}>
                  {colmenas.filter(c => c.activa).map(c => (
                    <label key={c.id} style={{
                      ...S.checkRow,
                      ...(colmenasSelec.has(c.id) ? S.checkRowSelec : {}),
                    }}>
                      <input
                        type="checkbox"
                        checked={colmenasSelec.has(c.id)}
                        onChange={() => toggleColmena(c.id)}
                        style={{ accentColor: '#E8922A', width: 16, height: 16, flexShrink: 0 }}
                      />
                      <span style={{ flex: 1, fontSize: 14, color: '#2C1A0E' }}>{c.nombre}</span>
                      {c.zona_nombre && (
                        <span style={{ fontSize: 12, color: '#8B6355' }}>{c.zona_nombre}</span>
                      )}
                    </label>
                  ))}
                </div>
                {colmenasSelec.size === 0 && (
                  <div style={{ fontSize: 12, color: '#C0392B', marginTop: 4 }}>{t('bitacora.noHiveSelected')}</div>
                )}
              </div>
            )}

            {form.alcance === 'zona' && (
              <div className="form-group">
                <label className="form-label">{t('zonas.title')}</label>
                <select value={form.zona_id} onChange={e => set('zona_id', e.target.value)} className="form-input">
                  <option value="">{t('bitacora.selectPlaceholder')}</option>
                  {zonas.map(z => <option key={z.id} value={z.id}>{z.nombre}</option>)}
                </select>
              </div>
            )}

            {form.tipo === 'inspeccion' && (
              <>
                <div className="form-group">
                  <label className="form-label">
                    {t('bitacora.behavior')} <span style={{ fontSize: 11, color: '#8B6355', fontWeight: 400 }}>{t('bitacora.optional')}</span>
                  </label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
                    {[
                      { v: 1, label: t('bitacora.behaviorAggressive') },
                      { v: 2, label: t('bitacora.behaviorNervous') },
                      { v: 3, label: t('bitacora.behaviorCalm') },
                      { v: 4, label: t('bitacora.behaviorDocile') },
                    ].map(b => (
                      <button key={b.v} type="button"
                        onClick={() => set('comportamiento', form.comportamiento === b.v ? null : b.v)}
                        style={{
                          ...S.comportBtn,
                          ...(form.comportamiento === b.v ? S.comportBtnActive : {}),
                        }}>
                        {b.icon} {b.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">
                    {t('bitacora.food')} <span style={{ fontSize: 11, color: '#8B6355', fontWeight: 400 }}>{t('bitacora.optional')}</span>
                  </label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      type="number" min="0" step="0.1"
                      value={form.alimento_cantidad}
                      onChange={e => set('alimento_cantidad', e.target.value)}
                      className="form-input" placeholder={t('bitacora.quantity')}
                      style={{ flex: 1 }}
                    />
                    <select value={form.alimento_unidad} onChange={e => set('alimento_unidad', e.target.value)}
                      className="form-input" style={{ width: 80 }}>
                      <option value="kg">kg</option>
                      <option value="L">L</option>
                    </select>
                  </div>
                </div>
              </>
            )}

            {form.tipo === 'alza' && (
              <>
                <div className="form-group">
                  <label className="form-label">{t('bitacora.alzaSection')}</label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    {[
                      { v: 'colocada', icon: '⬆️', label: t('bitacora.alzaColoca') },
                      { v: 'retirada', icon: '⬇️', label: t('bitacora.alzaRetira') },
                    ].map(b => (
                      <button key={b.v} type="button"
                        onClick={() => set('alza_accion', b.v)}
                        style={{
                          ...S.comportBtn,
                          fontSize: 14,
                          ...(form.alza_accion === b.v ? { background: '#EDE8F8', borderColor: '#6B4FA0', color: '#6B4FA0', fontWeight: 600 } : {}),
                        }}>
                        {b.icon} {b.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">{t('bitacora.alzaCantidad')}</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {[1, 2, 3].map(n => (
                      <button key={n} type="button"
                        onClick={() => set('alza_cantidad', String(n))}
                        style={{
                          flex: 1, padding: '10px 0', border: '1.5px solid',
                          borderRadius: 8, cursor: 'pointer', fontSize: 15, fontWeight: 600,
                          borderColor: form.alza_cantidad === String(n) ? '#6B4FA0' : '#F0EDE9',
                          background: form.alza_cantidad === String(n) ? '#EDE8F8' : 'white',
                          color: form.alza_cantidad === String(n) ? '#6B4FA0' : '#5C3D2E',
                          fontFamily: "'DM Sans', sans-serif",
                        }}>
                        {n}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    {t('bitacora.alzaNotes')} <span style={{ fontSize: 11, color: '#8B6355', fontWeight: 400 }}>{t('bitacora.optional')}</span>
                  </label>
                  <input
                    type="text"
                    value={form.alza_notas}
                    onChange={e => set('alza_notas', e.target.value)}
                    className="form-input"
                    placeholder={t('bitacora.alzaNotesPlaceholder')}
                  />
                  <VoiceInput onAppend={(text) => setForm(f => ({ ...f, alza_notas: (f.alza_notas ? f.alza_notas + ' ' : '') + text }))} />
                </div>
              </>
            )}

            {form.tipo === 'cosecha' && (
              <>
                <div className="form-group">
                  <label className="form-label">{t('cosecha.kgLabel')}</label>
                  <input type="number" min="0" step="0.1" className="form-input"
                    value={form.cosecha_kg} onChange={e => set('cosecha_kg', e.target.value)}
                    placeholder={t('cosecha.kgPlaceholder')} />
                  <div style={{ fontSize: 11, color: '#8B6355', marginTop: 4 }}>{t('cosecha.kgHint')}</div>
                </div>
                {form.cosecha_kg !== '' && parseFloat(form.cosecha_kg) === 0 && (
                  <>
                    <div className="form-group">
                      <label className="form-label">{t('cosecha.motivoLabel')}</label>
                      <select className="form-input" value={form.cosecha_motivo}
                        onChange={e => set('cosecha_motivo', e.target.value)}>
                        <option value="">{t('cosecha.motivoPlaceholder')}</option>
                        <option value="enjambrazon">{t('cosecha.motivo_enjambrazon')}</option>
                        <option value="colonia_debil">{t('cosecha.motivo_colonia_debil')}</option>
                        <option value="perdida_reina">{t('cosecha.motivo_perdida_reina')}</option>
                        <option value="mal_clima">{t('cosecha.motivo_mal_clima')}</option>
                        <option value="otro">{t('cosecha.motivo_otro')}</option>
                      </select>
                    </div>
                    {form.cosecha_motivo === 'otro' && (
                      <div className="form-group">
                        <label className="form-label">{t('cosecha.motivoOtroLabel')}</label>
                        <input className="form-input" value={form.cosecha_motivo_otro}
                          onChange={e => set('cosecha_motivo_otro', e.target.value)}
                          placeholder={t('cosecha.motivoOtroPlaceholder')} />
                      </div>
                    )}
                  </>
                )}
                {avisosCarencia.map(a => a.tratamiento_reciente && (
                  <div key={a.colmena_id} style={S.avisoBox}>
                    ⚠️ <strong>{nombreColmena(a.colmena_id)}:</strong>{' '}
                    {a.tratamiento_reciente.sustancia
                      ? t('avisos.cosechaTratamiento', {
                          sustancia: a.tratamiento_reciente.sustancia === 'otro'
                            ? (a.tratamiento_reciente.sustancia_otro || t('tratamiento.sust_otro'))
                            : t(`tratamiento.sust_${a.tratamiento_reciente.sustancia}`),
                          fecha: fmtFecha(a.tratamiento_reciente.fecha),
                        })
                      : t('avisos.cosechaTratamientoSinSustancia', { fecha: fmtFecha(a.tratamiento_reciente.fecha) })}
                  </div>
                ))}
              </>
            )}

            {form.tipo === 'tratamiento' && (
              <>
                <div className="form-group">
                  <label className="form-label">{t('tratamiento.sustanciaLabel')}</label>
                  <select className="form-input" value={form.trat_sustancia}
                    onChange={e => set('trat_sustancia', e.target.value)}>
                    <option value="">{t('tratamiento.sustanciaPlaceholder')}</option>
                    {SUSTANCIAS.map(g => (
                      <optgroup key={g.grupo} label={t(`tratamiento.grupo_${g.grupo}`)}>
                        {g.items.map(s => (
                          <option key={s} value={s}>{t(`tratamiento.sust_${s}`)}</option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </div>
                {form.trat_sustancia === 'otro' && (
                  <div className="form-group">
                    <label className="form-label">{t('tratamiento.sustanciaOtroLabel')}</label>
                    <input className="form-input" value={form.trat_sustancia_otro}
                      onChange={e => set('trat_sustancia_otro', e.target.value)}
                      placeholder={t('tratamiento.sustanciaOtroPlaceholder')} />
                  </div>
                )}
                {form.trat_sustancia && (() => {
                  const bio = bioDeSustancia(form.trat_sustancia);
                  const bg = bio === true ? '#EAF7EE' : bio === false ? '#FDECEA' : '#FDF6E3';
                  const bd = bio === true ? '#A9DFBF' : bio === false ? '#F5B7B1' : '#FAE5A0';
                  const fg = bio === true ? '#1E8449' : bio === false ? '#C0392B' : '#7D6608';
                  return (
                    <div style={{ padding: '8px 12px', borderRadius: 10, marginBottom: 14, background: bg, border: `1px solid ${bd}`, fontSize: 13, fontWeight: 600, color: fg }}>
                      {bio === true ? `🌿 ${t('tratamiento.bioApto')}` : bio === false ? `⚠️ ${t('tratamiento.bioNoApto')}` : `❔ ${t('tratamiento.bioVerificar')}`}
                    </div>
                  );
                })()}
                <div className="form-group" style={{ display: 'flex', gap: 8 }}>
                  <div style={{ flex: 1 }}>
                    <label className="form-label">{t('tratamiento.dosisLabel')}</label>
                    <input className="form-input" value={form.trat_dosis}
                      onChange={e => set('trat_dosis', e.target.value)}
                      placeholder={t('tratamiento.dosisPlaceholder')} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label className="form-label">{t('tratamiento.fechaFinLabel')}</label>
                    <input type="date" className="form-input" value={form.trat_fecha_fin}
                      min={form.fecha || undefined}
                      onChange={e => set('trat_fecha_fin', e.target.value)} />
                  </div>
                </div>
                {form.trat_fecha_fin && form.fecha && form.trat_fecha_fin < form.fecha && (
                  <div style={{ fontSize: 12, color: '#C0392B', marginTop: -8, marginBottom: 10 }}>{t('tratamiento.errorFechaFin')}</div>
                )}
                {avisosCarencia.map(a => a.alza_activa && (
                  <div key={a.colmena_id} style={S.avisoBox}>
                    ⚠️ <strong>{nombreColmena(a.colmena_id)}:</strong>{' '}
                    {t('avisos.tratamientoAlza', { fecha: fmtFecha(a.alza_activa.desde) })}
                  </div>
                ))}
              </>
            )}

            {form.tipo === 'varroa' && (
              <>
                <div className="form-group">
                  <label className="form-label">{t('varroa.metodoLabel')}</label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    {[
                      { v: 'gemuell', label: t('varroa.metodoGemuell') },
                      { v: 'puderzucker', label: t('varroa.metodoPuder') },
                      { v: 'alcohol', label: t('varroa.metodoAlcohol') },
                      { v: 'drohnenbrut', label: t('varroa.metodoDrohnen') },
                    ].map(m => (
                      <button key={m.v} type="button" onClick={() => set('varroa_metodo', m.v)}
                        style={{
                          ...S.comportBtn, fontSize: 13,
                          ...(form.varroa_metodo === m.v ? { background: '#FBEAE2', borderColor: '#B7410E', color: '#B7410E', fontWeight: 600 } : {}),
                        }}>
                        {m.label}
                      </button>
                    ))}
                  </div>
                  <div style={{ fontSize: 12, color: '#8B6355', marginTop: 6 }}>
                    {t(`varroa.desc_${form.varroa_metodo}`)}
                  </div>
                </div>

                {form.varroa_metodo === 'gemuell' && (
                  <>
                    <div className="form-group" style={{ display: 'flex', gap: 8 }}>
                      <div style={{ flex: 1 }}>
                        <label className="form-label">{t('varroa.fechaColocacion')}</label>
                        <input type="date" className="form-input" value={form.varroa_fecha_colocacion}
                          onChange={e => set('varroa_fecha_colocacion', e.target.value)} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label className="form-label">{t('varroa.fechaLectura')}</label>
                        <input type="date" className="form-input" value={form.varroa_fecha_lectura}
                          min={form.varroa_fecha_colocacion || undefined}
                          onChange={e => set('varroa_fecha_lectura', e.target.value)} />
                      </div>
                    </div>
                    {form.varroa_fecha_colocacion && form.varroa_fecha_lectura && form.varroa_fecha_lectura < form.varroa_fecha_colocacion && (
                      <div style={{ fontSize: 12, color: '#C0392B', marginTop: -8, marginBottom: 10 }}>{t('varroa.errorFechas')}</div>
                    )}
                    <div className="form-group">
                      <label className="form-label">{t('varroa.acarosTotal')}</label>
                      <input type="number" min="0" step="1" className="form-input" value={form.varroa_acaros}
                        onChange={e => set('varroa_acaros', e.target.value)} placeholder="0" />
                    </div>
                  </>
                )}

                {(form.varroa_metodo === 'puderzucker' || form.varroa_metodo === 'alcohol') && (
                  <div className="form-group" style={{ display: 'flex', gap: 8 }}>
                    <div style={{ flex: 1 }}>
                      <label className="form-label">{t('varroa.acaros')}</label>
                      <input type="number" min="0" step="1" className="form-input" value={form.varroa_acaros}
                        onChange={e => set('varroa_acaros', e.target.value)} placeholder="0" />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label className="form-label">{t('varroa.muestra')}</label>
                      <input type="number" min="1" step="1" className="form-input" value={form.varroa_muestra}
                        onChange={e => set('varroa_muestra', e.target.value)} placeholder="100" />
                    </div>
                  </div>
                )}

                {varroaEval && varroaEval.valor != null && (
                  <div style={{
                    padding: '10px 14px', borderRadius: 10, marginBottom: 14,
                    background: varroaEval.nivel === 'critico' ? '#FDECEA' : varroaEval.nivel === 'alerta' ? '#FDF6E3' : '#EAF7EE',
                    border: '1px solid ' + (varroaEval.nivel === 'critico' ? '#F5B7B1' : varroaEval.nivel === 'alerta' ? '#FAE5A0' : '#A9DFBF'),
                  }}>
                    <div style={{ fontWeight: 600, fontSize: 14, color: varroaEval.nivel === 'critico' ? '#C0392B' : varroaEval.nivel === 'alerta' ? '#B7950B' : '#1E8449' }}>
                      {varroaEval.valor} {varroaEval.unidad === 'pct' ? '%' : t('varroa.unidadAcarosDia')} · {t(`varroa.nivel_${varroaEval.nivel}`)}
                    </div>
                    {varroaEval.umbral && (
                      <div style={{ fontSize: 12, color: '#5C3D2E', marginTop: 4 }}>
                        {t('varroa.umbralInfo', {
                          alerta: varroaEval.umbral.alerta, critico: varroaEval.umbral.critico,
                          estacion: t(`varroa.estacion_${varroaEval.estacion}`),
                          unidad: varroaEval.unidad === 'pct' ? '%' : t('varroa.unidadAcarosDia'),
                        })}
                      </div>
                    )}
                    <div style={{ fontSize: 11, color: '#8B6355', marginTop: 6, fontStyle: 'italic' }}>
                      {t('varroa.orientacion', { fuente: varroaEval.fuente })}
                    </div>
                  </div>
                )}

                {varroaEval && varroaEval.tratamiento_reciente && (
                  <div style={{
                    padding: '10px 14px', borderRadius: 10, marginBottom: 14,
                    background: '#FDF6E3', border: '1px solid #FAE5A0', fontSize: 12, color: '#7D6608',
                  }}>
                    ⚠️ {t('varroa.tratamientoReciente', {
                      titulo: varroaEval.tratamiento_reciente.titulo,
                      fecha: varroaEval.tratamiento_reciente.fecha ? new Date(varroaEval.tratamiento_reciente.fecha).toLocaleDateString() : '',
                    })}
                  </div>
                )}
              </>
            )}

            <div className="form-group">
              <label className="form-label">{t('bitacora.notesLabel')}</label>
              <textarea value={form.descripcion} onChange={e => set('descripcion', e.target.value)}
                className="form-input" placeholder={t('bitacora.observationsPlaceholder')} />
              <VoiceInput onAppend={(text) => setForm(f => ({ ...f, descripcion: (f.descripcion ? f.descripcion + ' ' : '') + text }))} />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-ghost" onClick={onClose}>{t('common.cancel')}</button>
            <button type="submit" className="btn btn-primary"
              disabled={saving || (form.alcance === 'colmena' && colmenasSelec.size === 0)}>
              {saving ? t('common.saving') : isEdit ? t('bitacora.saveBtn') : colmenasSelec.size > 1 ? t('bitacora.registerInHives', { count: colmenasSelec.size }) : t('bitacora.newBtn')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const S = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 },
  title: { fontSize: 26, fontFamily: "'DM Sans', sans-serif" },
  sub: { color: '#8B6355', fontSize: 14, marginTop: 4 },
  filtros: { display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap', alignItems: 'center' },
  truncadoAviso: { background: '#FDF6E3', border: '1.5px solid #FAE5A0', color: '#7D6608', borderRadius: 10, padding: '10px 14px', fontSize: 13, marginBottom: 16, lineHeight: 1.5 },
  tipoFiltros: { display: 'flex', gap: 6, flexWrap: 'wrap' },
  tipoPill: { padding: '5px 12px', borderRadius: 20, border: '1.5px solid #FAE0B0', background: 'white', cursor: 'pointer', fontSize: 13, color: '#8B6355', transition: 'all 0.15s' },
  tipoPillActive: { background: '#FDF0DC', color: '#C97820', borderColor: '#E8922A', fontWeight: 500 },
  fechaLabel: { fontSize: 12, fontWeight: 600, color: '#8B6355', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10, paddingBottom: 6, borderBottom: '1px solid #F0EDE9' },
  eventoCard: { display: 'flex', flexDirection: 'column', background: 'white', borderRadius: 12, padding: '12px 16px', boxShadow: '0 1px 6px rgba(44,26,14,0.08)', border: '1px solid rgba(232,146,42,0.10)' },
  tipoIcon: { width: 36, height: 36, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 },
  tipoBadge: { padding: '1px 8px', borderRadius: 20, fontSize: 11, fontWeight: 500, marginLeft: 8 },
  editBtn: { background: 'none', border: 'none', cursor: 'pointer', color: '#C0A090', fontSize: 13, padding: '2px 6px', borderRadius: 6 },
  deleteBtn: { background: 'none', border: 'none', cursor: 'pointer', color: '#C0A090', fontSize: 13, padding: '2px 6px', borderRadius: 6 },
  exportMenu: { position: 'absolute', top: '100%', right: 0, background: 'white', borderRadius: 10, boxShadow: '0 4px 20px rgba(44,26,14,0.15)', padding: '6px', zIndex: 10, minWidth: 220, marginTop: 4, border: '1px solid #FAE0B0' },
  exportItem: { display: 'block', width: '100%', padding: '8px 14px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 13, color: '#2C1A0E', borderRadius: 6, textAlign: 'left' },
  tipoBtn: { padding: '8px 6px', border: '1.5px solid #FAE0B0', borderRadius: 8, background: 'white', cursor: 'pointer', fontSize: 12, transition: 'all 0.15s', fontFamily: "'DM Sans', sans-serif", minWidth: 0, whiteSpace: 'normal', overflowWrap: 'break-word', lineHeight: 1.3 },
  checkList: { display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 220, overflowY: 'auto', padding: '2px 0' },
  checkRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 8, border: '1.5px solid #F0EDE9', background: 'white', cursor: 'pointer', transition: 'all 0.12s' },
  checkRowSelec: { border: '1.5px solid #E8922A', background: '#FFF8EE' },
  selecBadge: { marginLeft: 8, fontSize: 11, fontWeight: 600, background: '#FFF0D4', color: '#C97820', padding: '2px 8px', borderRadius: 10 },
  toggleTodasBtn: { fontSize: 12, color: '#E8922A', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, padding: 0, fontFamily: "'DM Sans', sans-serif", flexShrink: 0 },
  comportBtn: { padding: '8px 6px', border: '1.5px solid #F0EDE9', borderRadius: 8, background: 'white', cursor: 'pointer', fontSize: 13, fontFamily: "'DM Sans', sans-serif", transition: 'all 0.15s', color: '#5C3D2E' },
  comportBtnActive: { background: '#FFF0D4', borderColor: '#E8922A', color: '#C97820', fontWeight: 600 },
  infoChip: { fontSize: 12, background: '#F5F0E8', color: '#5C3D2E', padding: '2px 10px', borderRadius: 20, border: '1px solid #EAE0D0' },
  avisoBox: { padding: '10px 12px', borderRadius: 10, marginBottom: 14, background: '#FDF6E3', border: '1px solid #FAE5A0', fontSize: 13, color: '#7D6608', lineHeight: 1.5 },
  fotosToggle: { background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: '#8B6355', padding: 0, display: 'inline-flex', alignItems: 'center', gap: 4, fontFamily: "'DM Sans', sans-serif" },
  fotosGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 },
  fotoThumb: { aspectRatio: '1', borderRadius: 8, overflow: 'hidden', cursor: 'pointer', background: '#F0EDE9', border: '1px solid #EAE0D0' },
  addFotoBtn: { padding: '5px 12px', border: '1.5px dashed #FAE0B0', borderRadius: 8, background: '#FFFBF5', cursor: 'pointer', fontSize: 12, color: '#C97820', fontFamily: "'DM Sans', sans-serif", transition: 'all 0.15s' },
  visorOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 },
  visorContent: { display: 'flex', flexDirection: 'column', alignItems: 'center', maxWidth: '100%' },
  periodoTabs: { display: 'flex', background: '#F0EDE9', borderRadius: 8, padding: 3, gap: 2 },
  periodoTab: { padding: '6px 14px', borderRadius: 6, border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13, color: '#8B6355', transition: 'all 0.15s', fontFamily: "'DM Sans', sans-serif" },
  periodoTabActive: { background: 'white', color: '#2C1A0E', fontWeight: 500, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  navBtn: { padding: '5px 12px', borderRadius: 7, border: '1.5px solid #FAE0B0', background: 'white', cursor: 'pointer', fontSize: 14, color: '#5C3D2E', fontFamily: "'DM Sans', sans-serif" },
  sinActividad: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 10, border: '1.5px dashed #E8D5B0', background: '#FDFAF5' },
};
